import React, { useCallback, useEffect, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { exportCrossoverHistory } from '../../lib/excel-export'
import { ExcelDownloadButton } from '../ui/ExcelDownloadButton'
import { COLUMN_IDS, CROSSOVER_COLUMNS, FIELD_KEYS, getFieldValue } from '../../config/columns'
import { CONFIG } from '../../config/constants'
import { useColumnVisibility } from '../../hooks/useColumnVisibility'
import { useSortedData } from '../../hooks/useSortedData'
import { formatDateToDDMMYYYY, formatISTTime } from '../../lib/date-utils'
import
    {
        formatCount,
        getCountColorClass,
        getShiftColorClass,
        getVarianceColorClass,
        TABLE_CLASSES,
    } from '../../lib/table-utils'
import { type ColumnConfig, ColumnSelector } from '../ColumnSelector'
import { ShiftChip } from '../chips/ShiftChip'
import { VarianceChip } from '../chips/VarianceChip'
import { NumberCell } from '../ui/ghost-table/DataCells'
import { GhostTh } from '../ui/ghost-table/StickyHeader'
import { StrikeChip } from '../ui/ghost-table/StrikeChip'
// Ghost Components
import { TableWrapper } from '../ui/ghost-table/TableWrapper'
import { WidgetErrorBoundary } from '../ui/WidgetErrorBoundary'
import { TableVirtuoso } from 'react-virtuoso'

interface CrossoverHistoryTableProps
{
    data: any[]
    title?: string
    symbol?: string
    mode?: string
    isLoading?: boolean
    rowsPerPage?: number
    onRowsPerPageChange?: ( rows: number ) => void
}

const CrossoverRow = React.memo( ( { row, idx, isVisible }: { row: any, idx: number, isVisible: ( id: string ) => boolean } ) =>
{
    const tdClass = "p-2 h-[48px] align-middle whitespace-nowrap border-b border-slate-100/30 dark:border-slate-800/30"

    // Extract Data
    const rank = row.rank || row.Rank || 0
    const ceLtp = row.ceLtp ?? row.CE_LTP
    const peLtp = row.peLtp ?? row.PE_LTP

    // Values (OI/Vol)
    const resValue = row.resistanceValue ?? getFieldValue( row, FIELD_KEYS.RESISTANCE_VALUE ) ?? 0
    const supValue = row.supportValue ?? getFieldValue( row, FIELD_KEYS.SUPPORT_VALUE ) ?? 0

    const resStrike = row.resistanceStrike ?? getFieldValue( row, FIELD_KEYS.RESISTANCE )
    const resStrike2 = row.resistanceStrike2 ?? getFieldValue( row, FIELD_KEYS.RESISTANCE_2 )
    const supStrike = row.supportStrike ?? getFieldValue( row, FIELD_KEYS.SUPPORT )
    const supStrike2 = row.supportStrike2 ?? getFieldValue( row, FIELD_KEYS.SUPPORT_2 )

    const ceCount = row.ceCount ?? row.ce_count ?? 0
    const peCount = row.peCount ?? row.pe_count ?? 0
    const finalCount = row.totalCount ?? row.total_count ?? ceCount + peCount

    const variance = typeof row.variance === 'number' ? row.variance : ( row.variance?.finalVariance ?? row.finalVariance ?? 0 )
    const shift = row.shift ?? 0
    const shiftOld = row.shiftOld ?? row.shift_old ?? 0
    const atmStrike = row.atm ?? row.atmStrike ?? 0
    const maxDiff = row.maxDiff ?? row.max_diff ?? 0
    const minDiff = row.minDiff ?? row.min_diff ?? 0

    return (
        <>
            <td className={ `${tdClass} pl-4 font-mono font-medium text-slate-600 dark:text-slate-400` }>
                { ( () =>
                {
                    let time = '-'
                    let date = ''
                    const val = String( row.timestampIST || row.timestamp || row.TimeStamp || row.created_at || '' )
                    try
                    {
                        time = formatISTTime( val ) || val
                        if ( typeof ( row.timestamp || row.TimeStamp ) === 'number' )
                        {
                            date = formatDateToDDMMYYYY( new Date( row.timestamp || row.TimeStamp ) )
                        } else
                        {
                            if ( val.includes( 'T' ) ) date = val.split( 'T' )[ 0 ] ?? ''
                            else if ( val.includes( ' ' ) ) date = val.split( ' ' )[ 0 ] ?? ''
                        }
                        if ( time.includes( 'T' ) ) time = time.split( 'T' )[ 1 ]?.substring( 0, 5 ) ?? ''
                        if ( time.includes( ' ' ) ) time = time.split( ' ' )[ 1 ]?.substring( 0, 5 ) ?? ''
                    } catch (e) { console.warn('Ignored error in CrossoverHistoryTable.tsx:', e); }

                    return (
                        <div className="flex flex-col justify-center">
                            <span className="font-bold text-slate-700 dark:text-slate-200 text-xs">{ time }</span>
                            { date && date !== time && <span className="text-[9px] text-slate-400 font-medium">{ date }</span> }
                        </div>
                    )
                } )() }
            </td>

            { isVisible( COLUMN_IDS.RANK ) && (
                <td className={ `${tdClass} text-center` }>
                    <NumberCell value={ rank } decimals={ 0 } className="font-bold text-slate-700 dark:text-slate-300" />
                </td>
            ) }

            { isVisible( COLUMN_IDS.CE_LTP ) && (
                <td className={ `${tdClass} text-right` }><NumberCell value={ Number( ceLtp || 0 ) } decimals={ 2 } /></td>
            ) }

            { isVisible( COLUMN_IDS.RESISTANCE_VALUE ) && (
                <td className={ `${tdClass} text-center` }><NumberCell value={ resValue } className="text-rose-600 font-bold" /></td>
            ) }

            { isVisible( COLUMN_IDS.RES_STRIKE ) && (
                <td className={ `${tdClass} text-center shadow-[inset_-1px_0_0_0_rgba(244,63,94,0.1)]` }><StrikeChip strike={ resStrike } type="resistance" /></td>
            ) }

            { isVisible( COLUMN_IDS.RES_STRIKE_2 ) && (
                <td className={ `${tdClass} text-center` }><StrikeChip strike={ resStrike2 } type="resistance" /></td>
            ) }

            { isVisible( COLUMN_IDS.ATM ) && (
                <td className={ `${tdClass} text-center bg-slate-50/30 dark:bg-slate-800/20 shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.05)]` }><StrikeChip strike={ atmStrike } type="atm" /></td>
            ) }

            { isVisible( COLUMN_IDS.SUP_STRIKE ) && (
                <td className={ `${tdClass} text-center shadow-[inset_1px_0_0_0_rgba(16,185,129,0.1)]` }><StrikeChip strike={ supStrike } type="support" /></td>
            ) }

            { isVisible( COLUMN_IDS.SUP_STRIKE_2 ) && (
                <td className={ `${tdClass} text-center` }><StrikeChip strike={ supStrike2 } type="support" /></td>
            ) }

            { isVisible( COLUMN_IDS.SUPPORT_VALUE ) && (
                <td className={ `${tdClass} text-center` }><NumberCell value={ supValue } className="text-emerald-600 font-bold" /></td>
            ) }

            { isVisible( COLUMN_IDS.PE_LTP ) && (
                <td className={ `${tdClass} text-right` }><NumberCell value={ Number( peLtp || 0 ) } decimals={ 2 } /></td>
            ) }

            { isVisible( COLUMN_IDS.CE_COUNT ) && (
                <td className={ `${tdClass} text-center` }><NumberCell value={ ceCount } decimals={ 0 } showSign={ true } /></td>
            ) }

            { isVisible( COLUMN_IDS.PE_COUNT ) && (
                <td className={ `${tdClass} text-center` }><NumberCell value={ peCount } decimals={ 0 } showSign={ true } /></td>
            ) }

            { isVisible( COLUMN_IDS.FINAL_COUNT ) && (
                <td className={ `${tdClass} text-center` }><NumberCell value={ finalCount } decimals={ 0 } showSign={ true } /></td>
            ) }

            { isVisible( COLUMN_IDS.MAX_OI_DIFF ) && (
                <td className={ `${tdClass} text-right` }><NumberCell value={ maxDiff } decimals={ 2 } suffix="%" /></td>
            ) }

            { isVisible( COLUMN_IDS.MIN_OI_DIFF ) && (
                <td className={ `${tdClass} text-right` }><NumberCell value={ minDiff } decimals={ 2 } suffix="%" /></td>
            ) }

            { isVisible( COLUMN_IDS.VARIANCE ) && (
                <td className={ `${tdClass} text-center` }><VarianceChip value={ variance } className={ getVarianceColorClass( variance ) } /></td>
            ) }

            { isVisible( COLUMN_IDS.SHIFT ) && (
                <td className={ `${tdClass} text-center bg-indigo-50/20 dark:bg-indigo-900/10` }><ShiftChip value={ shift } className={ getShiftColorClass( shift ) } /></td>
            ) }

            { isVisible( COLUMN_IDS.SHIFT_OLD ) && (
                <td className={ `${tdClass} text-center` }><ShiftChip value={ shiftOld } className={ getShiftColorClass( shiftOld ) } /></td>
            ) }

            <td className={ `${tdClass} text-center pr-4 text-slate-300 group-hover:text-slate-400 transition-colors` }>
                <ChevronRight className="h-4 w-4 ml-auto" />
            </td>
        </>
    )
} )

const CrossoverHistoryTableContent: React.FC<CrossoverHistoryTableProps> = ( {
    data,
    title,
    symbol,
    rowsPerPage: controlledRowsPerPage,
    onRowsPerPageChange,
} ) =>
{
    const handleExport = useCallback( () =>
    {
        exportCrossoverHistory( data || [], symbol || 'Unknown' )
    }, [ data, symbol ] )
    // Pagination & Sort State
    const [ visibleLimit, setVisibleLimit ] = useState( 10 )
    const [ internalRowsPerPage, setInternalRowsPerPage ] = useState( 10 )

    useEffect( () =>
    {
        const saved = localStorage.getItem( CONFIG.UI.STORAGE_KEYS.CROSSOVER_HISTORY_ROWS )
        if ( saved )
        {
            const parsed = parseInt( saved, 10 )
            setInternalRowsPerPage( parsed )
            setVisibleLimit( parsed )
        }
    }, [] )

    const rowsPerPage = controlledRowsPerPage ?? internalRowsPerPage

    // Sync visibleLimit when rowsPerPage changes
    useEffect( () =>
    {
        if ( rowsPerPage ) setVisibleLimit( rowsPerPage )
    }, [ rowsPerPage ] )

    // Use centralized column visibility hook with lazy initialization
    const defaultColumns: ColumnConfig[] = CROSSOVER_COLUMNS.map( ( c ) => ( { id: c.id, label: c.label, visible: true } ) )
    const [ columns, toggleColumn, isVisible ] = useColumnVisibility(
        CONFIG.UI.STORAGE_KEYS.CROSSOVER_HISTORY_COLUMNS,
        defaultColumns,
    )

    // Save rows per page
    useEffect( () =>
    {
        // Only save to local storage if we are controlling it internally or just tracking preference
        if ( rowsPerPage && typeof window !== 'undefined' )
        {
            localStorage.setItem( CONFIG.UI.STORAGE_KEYS.CROSSOVER_HISTORY_ROWS, rowsPerPage.toString() )
        }
    }, [ rowsPerPage ] )

    // Sorting Logic
    const { sortedData: items, requestSort } = useSortedData( data || [], FIELD_KEYS.TIMESTAMP[ 0 ], 'desc' )

    // Pagination Logic
    const totalItems = items.length

    const deferredData = React.useDeferredValue( items )

    const handleRowsPerPageChange = ( e: React.ChangeEvent<HTMLSelectElement> ) =>
    {
        const newRows = Number( e.target.value )
        if ( onRowsPerPageChange )
        {
            onRowsPerPageChange( newRows )
        } else
        {
            setInternalRowsPerPage( newRows )
        }
    }

    const tdClass = 'p-3 align-middle border-b border-slate-100/50 dark:border-slate-800/50 whitespace-nowrap text-sm'
    const headerClass =
        'p-3 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 bg-slate-50/50 dark:bg-slate-800/50 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors select-none'

    return (
        <TableWrapper className="bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800 rounded-lg transition-colors duration-300">
            {/* Header Controls */ }
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex flex-wrap justify-between items-center gap-4 bg-white dark:bg-slate-900 rounded-t-lg transition-colors duration-300">
                <div className="flex items-center gap-2">
                    <h3 className="font-bold text-slate-700 dark:text-slate-200 text-sm md:text-base">
                        { title || 'Crossover History' }
                    </h3>
                    <span className="text-xs text-slate-400 dark:text-slate-500 font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                        { totalItems }
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={ rowsPerPage }
                        onChange={ handleRowsPerPageChange }
                        className="h-9 pl-3 pr-8 text-xs font-medium text-foreground bg-background/50 backdrop-blur-sm border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary cursor-pointer shadow-sm hover:border-muted-foreground/40 transition-colors"
                        title="Initial rows to load (Chunk size)"
                    >
                        { [ 10, 15, 25, 50, 100 ].map( ( n ) => (
                            <option key={ n } value={ n }>{ n } rows</option>
                        ) ) }
                    </select>
                    <ExcelDownloadButton onExport={ handleExport } title="Download Crossover History as Excel" />
                    <ColumnSelector columns={ columns } onChange={ toggleColumn } />
                </div>
            </div>

            <div className="relative overflow-hidden" style={ { height: Math.min( deferredData.length * 48 + 60, 520 ) } }>
                <TableVirtuoso
                    data={ deferredData }
                    overscan={ 8 }
                    style={ { height: '100%' } }
                    components={ {
                        Table: React.forwardRef<HTMLTableElement, React.TableHTMLAttributes<HTMLTableElement>>( ( props, ref ) => (
                            <table ref={ ref } { ...props } className="w-full text-left border-collapse" />
                        ) ),
                        TableHead: React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>( ( props, ref ) => (
                            <thead ref={ ref } { ...props } className="sticky top-0 z-10 bg-white dark:bg-slate-900 shadow-sm transition-colors duration-300" />
                        ) ),
                        TableRow: ( props ) => (
                            <tr { ...props } className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors" />
                        ),
                    } }
                    fixedHeaderContent={ () => (
                        <tr>
                            <th className={ `${headerClass} text-left pl-4` }>Date Time</th>
                            { isVisible( COLUMN_IDS.RANK ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( 'rank' ) }>Rank</th>
                            ) }
                            { isVisible( COLUMN_IDS.CE_LTP ) && (
                                <th className={ `${headerClass} text-right` } onClick={ () => requestSort( FIELD_KEYS.CE_LTP[ 0 ] ) }>CE LTP</th>
                            ) }
                            { isVisible( COLUMN_IDS.RESISTANCE_VALUE ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.RESISTANCE_VALUE[ 0 ] ) }>OI/Vol</th>
                            ) }
                            { isVisible( COLUMN_IDS.RES_STRIKE ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.RESISTANCE[ 0 ] ) }>Resistance</th>
                            ) }
                            { isVisible( COLUMN_IDS.RES_STRIKE_2 ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.RESISTANCE_2[ 0 ] ) }>Res 2</th>
                            ) }
                            { isVisible( COLUMN_IDS.ATM ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.ATM[ 0 ] ) }>ATM</th>
                            ) }
                            { isVisible( COLUMN_IDS.SUP_STRIKE ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.SUPPORT[ 0 ] ) }>Support</th>
                            ) }
                            { isVisible( COLUMN_IDS.SUP_STRIKE_2 ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.SUPPORT_2[ 0 ] ) }>Sup 2</th>
                            ) }
                            { isVisible( COLUMN_IDS.SUPPORT_VALUE ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.SUPPORT_VALUE[ 0 ] ) }>OI/Vol</th>
                            ) }
                            { isVisible( COLUMN_IDS.PE_LTP ) && (
                                <th className={ `${headerClass} text-right` } onClick={ () => requestSort( FIELD_KEYS.PE_LTP[ 0 ] ) }>PE LTP</th>
                            ) }
                            { isVisible( COLUMN_IDS.CE_COUNT ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.CE_COUNT[ 0 ] ) }>CE Count</th>
                            ) }
                            { isVisible( COLUMN_IDS.PE_COUNT ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.PE_COUNT[ 0 ] ) }>PE Count</th>
                            ) }
                            { isVisible( COLUMN_IDS.FINAL_COUNT ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.FINAL_COUNT[ 0 ] ) }>Final</th>
                            ) }
                            { isVisible( COLUMN_IDS.MAX_OI_DIFF ) && (
                                <th className={ `${headerClass} text-right` } onClick={ () => requestSort( FIELD_KEYS.MAX_OI_DIFF[ 0 ] ) }>Max Diff</th>
                            ) }
                            { isVisible( COLUMN_IDS.MIN_OI_DIFF ) && (
                                <th className={ `${headerClass} text-right` } onClick={ () => requestSort( FIELD_KEYS.MIN_OI_DIFF[ 0 ] ) }>Min Diff</th>
                            ) }
                            { isVisible( COLUMN_IDS.VARIANCE ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.VARIANCE[ 0 ] ) }>Variance</th>
                            ) }
                            { isVisible( COLUMN_IDS.SHIFT ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.SHIFT[ 0 ] ) }>Shift</th>
                            ) }
                            { isVisible( COLUMN_IDS.SHIFT_OLD ) && (
                                <th className={ `${headerClass} text-center` } onClick={ () => requestSort( FIELD_KEYS.SHIFT_OLD[ 0 ] ) }>Shift Old</th>
                            ) }
                            <th className={ `${headerClass} w-10` }></th>
                        </tr>
                    ) }
                    itemContent={ ( index, row ) => (
                        <CrossoverRow
                            row={ row }
                            idx={ index }
                            isVisible={ isVisible }
                        />
                    ) }
                />
            </div>
        </TableWrapper>
    )
}

export const CrossoverHistoryTable = ( props: CrossoverHistoryTableProps ) => (
    <WidgetErrorBoundary fallbackDescription="Crossover History">
        <CrossoverHistoryTableContent { ...props } />
    </WidgetErrorBoundary>
)
